import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { List } from 'immutable';

import { useHistory } from 'react-router-dom';
import { routes } from 'flow/types/routes';
import WavingHandIcon from 'flow/components/icons/WavingHandIcon';
import Page from 'flow/components/Setting/Page';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import { getIsSinglePaneMode } from 'flow/selectors/selectors';
import { saveCentralSwitchWidgets } from 'flow/utils/browser-utils';
import { getCentralSwitchWidgets } from 'flow/utils/browser-utils-TW';
import {
    CentralSwitchWidgetKey,
    selectAvailableCentralSwitchWidgets,
} from 'flow/utils/CentralSwitchUtils';
import { getBrandLogo } from 'flow/brands/brands';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';

const COLUMN_MIN_WIDTH = 312;
const BOX_MIN_HEIGHT = 185;
const WIDGET_GAP = 12;

const CentralSwitch = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { openModal } = useModal();

    const isSinglePaneMode = useAppSelector(getIsSinglePaneMode);
    const availableWidgets = useAppSelector(
        selectAvailableCentralSwitchWidgets,
    );
    const logoUrl = getBrandLogo('logo60LightMode');

    const [centralSwitchWidgets, setCentralSwitchWidgets] = useState<
        CentralSwitchWidgetKey[]
    >(
        getCentralSwitchWidgets().filter(
            (widgetId: CentralSwitchWidgetKey) => !!availableWidgets[widgetId],
        ),
    );

    useEffect(() => {
        setCentralSwitchWidgets(
            getCentralSwitchWidgets().filter(
                (widgetId: CentralSwitchWidgetKey) =>
                    !!availableWidgets[widgetId],
            ),
        );
    }, [availableWidgets]);

    const [numberOfColumns, setNumberOfColumns] = useState<number | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const computeColumns = useCallback((width: number) => {
        if (width <= 720) {
            return 2;
        }
        return Math.max(1, Math.floor(width / (COLUMN_MIN_WIDTH + WIDGET_GAP)));
    }, []);

    useEffect(() => {
        if (logoUrl && !centralSwitchWidgets.includes('RetailerLogo')) {
            saveWidgets([...centralSwitchWidgets, 'RetailerLogo']);
        }

        if (!logoUrl && centralSwitchWidgets.includes('RetailerLogo')) {
            saveWidgets(
                centralSwitchWidgets.filter(
                    (widgetId) => widgetId !== 'RetailerLogo',
                ),
            );
        }
    }, [logoUrl, centralSwitchWidgets]);

    // Compute initial column count synchronously before paint to avoid remounting widgets
    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        setNumberOfColumns(
            computeColumns(container.getBoundingClientRect().width),
        );
    }, [computeColumns]);

    // ResizeObserver for subsequent resize changes
    useEffect(() => {
        const container = containerRef.current;
        if (!container || centralSwitchWidgets.length === 0) return undefined;

        const observer = new ResizeObserver((entries) => {
            setNumberOfColumns(computeColumns(entries[0].contentRect.width));
        });
        observer.observe(container);
        return () => {
            observer.disconnect();
        };
    }, [centralSwitchWidgets.length, computeColumns]);

    const createWidget = (widgetId: CentralSwitchWidgetKey) => {
        const WidgetComponent = availableWidgets[widgetId]?.component;
        if (!WidgetComponent) return null;
        if (widgetId === 'RetailerLogo') {
            return (
                WidgetComponent && (
                    <div
                        key={widgetId}
                        className="shadow-base flex max-h-200 min-h-64 max-w-screen-sm min-w-72 grow flex-col items-center justify-center rounded-xl bg-white"
                    >
                        <WidgetComponent
                            logoUrl={logoUrl}
                            inheritedStyle={{
                                height: BOX_MIN_HEIGHT,
                            }}
                        />
                    </div>
                )
            );
        } else {
            return (
                WidgetComponent && (
                    <div
                        key={widgetId}
                        className="badge-bg shadow-base flex max-h-200 min-h-64 max-w-screen-sm min-w-72 grow flex-col rounded-xl bg-white dark:bg-gray-100"
                    >
                        <WidgetComponent />
                    </div>
                )
            );
        }
    };

    const saveWidgets = (widgetsToSaveAsActive: CentralSwitchWidgetKey[]) => {
        setCentralSwitchWidgets(widgetsToSaveAsActive);
        saveCentralSwitchWidgets(widgetsToSaveAsActive);
    };

    let widgetColumns: List<List<CentralSwitchWidgetKey>> = List();
    if (numberOfColumns) {
        centralSwitchWidgets.forEach((widgetId, index) => {
            const putInColumn = index % numberOfColumns;
            widgetColumns = widgetColumns.update(
                putInColumn,
                List<CentralSwitchWidgetKey>(),
                (columnList: List<CentralSwitchWidgetKey>) =>
                    columnList.push(widgetId),
            );
        });
    }

    return (
        <Page
            mainId="central-switch-overview"
            header={i18next.t('messages.overview')}
            childrenClassName="w-full"
            className="no-main-margin bg-gray-150 dark:bg-white"
            onClickBack={
                isSinglePaneMode
                    ? () => history.replace(routes['extensions'])
                    : undefined
            }
            action={
                <IconButton
                    tooltipLabel={i18next.t('settings')}
                    onClick={() =>
                        openModal('centralSwitchWidget', {
                            saveActiveWidgets: saveWidgets,
                            activeWidgetKeys: centralSwitchWidgets,
                            availableWidgets,
                        })
                    }
                >
                    <Icon iconKey="setting_menu" />
                </IconButton>
            }
        >
            <div className="flex grow flex-col overflow-x-hidden">
                {centralSwitchWidgets.length === 0 ? (
                    <div className="mt-3 flex grow flex-col items-center justify-center">
                        <WavingHandIcon className="!h-36 !w-36" />
                    </div>
                ) : (
                    <div
                        className="relative flex grow flex-col items-start overflow-y-auto px-6 pb-4"
                        ref={containerRef}
                    >
                        <div className="mx-auto flex w-full grow flex-wrap items-start justify-center gap-6">
                            {widgetColumns
                                .map((oneColumn) => (
                                    <div
                                        className="flex w-72 max-w-screen-sm grow flex-col gap-6"
                                        key={oneColumn.toArray().join()}
                                    >
                                        {oneColumn.map(createWidget).toArray()}
                                    </div>
                                ))
                                .toArray()}
                        </div>
                    </div>
                )}
            </div>
        </Page>
    );
};

export default CentralSwitch;
